import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, auth, fmtDate, inputCls, Field, Modal, PageHead, PrimaryBtn, StatusBadge, errMsg } from './finance/shared';
import { EmptyState } from '../../components/dashboard/TodaySchedule';
import Icon from '../../components/dashboard/DashboardIcons';

const TYPES = { league: 'دوري', knockout: 'خروج مغلوب', friendly: 'ودية', cup: 'كأس' };
const T_STATUS = { upcoming: 'قادمة', ongoing: 'جارية', completed: 'منتهية', cancelled: 'ملغاة' };

export default function TournamentsPage() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [formError, setFormError] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [toast, setToast] = useState(null);
  const notify = (m) => { setToast(m); setTimeout(() => setToast(null), 3000); };

  const load = useCallback(async () => {
    setLoading(true);
    const [t, c] = await Promise.allSettled([axios.get(`${API}/tournaments`, auth()), axios.get(`${API}/categories`, auth())]);
    if (t.status === 'fulfilled') setItems(t.value.data || []);
    if (c.status === 'fulfilled') setCats(c.value.data || []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const catName = (id) => cats.find((c) => c.id === id)?.name || '';
  const openForm = (t) => { setEditing(t || null); setForm(t ? { ...t } : { name: '', tournament_type: 'league', category_id: '', start_date: '', end_date: '', status: 'upcoming', location: '' }); setFormError(null); setModal(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.start_date || !form.category_id) { setFormError('الاسم والفئة وتاريخ البداية حقول مطلوبة'); return; }
    const payload = { name: form.name, tournament_type: form.tournament_type, category_id: form.category_id || null, start_date: form.start_date, end_date: form.end_date || null, status: form.status, location: form.location || null };
    try {
      if (editing) await axios.put(`${API}/tournaments/${editing.id}`, payload, auth());
      else await axios.post(`${API}/tournaments`, payload, auth());
      setModal(false); notify('تم الحفظ'); load();
    } catch (err) { setFormError(errMsg(err, 'فشل الحفظ')); }
  };

  const remove = async () => {
    try { await axios.delete(`${API}/tournaments/${confirmDel.id}`, auth()); notify('تم الحذف'); setConfirmDel(null); load(); }
    catch (err) { notify(errMsg(err, 'تعذر الحذف')); setConfirmDel(null); }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-4" data-testid="tournaments-page">
      {toast && <div className="fixed top-16 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-[#16233b] px-4 py-2 text-sm text-white shadow-lg">{toast}</div>}
      <PageHead title="البطولات" desc="بطولات المنشأة ومسابقاتها"
        action={<PrimaryBtn onClick={() => openForm(null)} data-testid="trn-add-btn"><Icon name="plus" className="w-4 h-4" /> إضافة بطولة</PrimaryBtn>} />

      {loading ? <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{[1, 2].map((i) => <div key={i} className="h-28 animate-pulse rounded-xl bg-slate-200/70" />)}</div>
        : items.length === 0 ? <div className="rounded-xl border border-slate-200 bg-white p-6"><EmptyState icon="trophy" text="لا توجد بطولات بعد" testId="trn-empty" /></div>
        : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((t) => (
              <div key={t.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid="tournament-card">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-bold text-slate-900">{t.name}</div>
                    <div className="mt-0.5 text-xs text-slate-500">{TYPES[t.tournament_type] || t.tournament_type}{catName(t.category_id) ? ` • ${catName(t.category_id)}` : ''}</div>
                  </div>
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-[#16233b]"><Icon name="trophy" className="w-4 h-4" /></span>
                </div>
                <div className="mt-2 text-xs text-slate-500">{fmtDate(t.start_date)}{t.end_date ? ` — ${fmtDate(t.end_date)}` : ''}</div>
                <div className="mt-3 flex items-center justify-between">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${t.status === 'ongoing' ? 'bg-teal-50 text-teal-700' : t.status === 'completed' ? 'bg-slate-100 text-slate-500' : 'bg-amber-50 text-amber-700'}`}>{T_STATUS[t.status] || t.status}</span>
                  <div className="flex gap-1">
                    <button onClick={() => openForm(t)} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100" data-testid="trn-edit-btn"><Icon name="cog" className="w-4 h-4" /></button>
                    <button onClick={() => setConfirmDel(t)} className="rounded-lg p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" data-testid="trn-del-btn"><Icon name="x" className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

      <Modal open={modal} title={editing ? 'تعديل البطولة' : 'إضافة بطولة'} onClose={() => setModal(false)} wide>
        {formError && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="trn-form-error">{formError}</div>}
        <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="اسم البطولة" required><input className={inputCls} value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="trn-input-name" /></Field>
          <Field label="النوع"><select className={inputCls} value={form.tournament_type || 'league'} onChange={(e) => setForm({ ...form, tournament_type: e.target.value })}>{Object.entries(TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
          <Field label="الفئة" required><select className={inputCls} value={form.category_id || ''} onChange={(e) => setForm({ ...form, category_id: e.target.value })}><option value="">—</option>{cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
          <Field label="الحالة"><select className={inputCls} value={form.status || 'upcoming'} onChange={(e) => setForm({ ...form, status: e.target.value })}>{Object.entries(T_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Field>
          <Field label="تاريخ البداية" required><input className={inputCls} type="date" value={form.start_date || ''} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="trn-input-start" /></Field>
          <Field label="تاريخ النهاية"><input className={inputCls} type="date" value={form.end_date || ''} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></Field>
          <div className="col-span-full flex justify-end gap-2">
            <button type="button" onClick={() => setModal(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" data-testid="trn-form-submit">حفظ</PrimaryBtn>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(confirmDel)} title="حذف البطولة" onClose={() => setConfirmDel(null)}>
        <p className="text-sm text-slate-600">حذف «{confirmDel?.name}» قد يؤثر على المباريات المرتبطة بها. متأكد؟</p>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setConfirmDel(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
          <button onClick={remove} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white" data-testid="trn-confirm-del">حذف</button>
        </div>
      </Modal>
    </div>
  );
}
